import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Upper bound on how long we will wait for the closing animation before tearing
 * the dialog down anyway. The animation itself is --animation-speed (125ms), so
 * this is a ~4x margin: long enough that the animation always wins in normal
 * conditions, short enough that a stalled animation cannot strand the page.
 */
const CLOSE_ANIMATION_TIMEOUT = 500;

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** Guards against closeDialog running twice concurrently. */
  #closing = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Open synchronously — do NOT gate this on a frame.
    //
    // requestAnimationFrame callbacks are not delivered while the browser is
    // withholding frames (backgrounded tab, in-app browsers such as Instagram,
    // heavy throttling). The add-to-cart fetch, the cart bubble count and the
    // Section Rendering morph are none of them frame-gated, so the shopper saw
    // the item added and the badge tick to 1 while the drawer stayed shut. Every
    // later attempt was dead too, because the cart badge's open() and quick-add
    // both route through this same method.
    //
    // The one DOM read (window.scrollY) is taken above, so no read-after-write
    // thrash remains for rAF to batch. Keeping the scroll lock in here with the
    // open also means the two can never separate: deferring only the lock would
    // leave a modal open over a still-scrollable page whenever frames stall.
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());

    // Attach the dismiss handlers on the NEXT task, never in this one.
    //
    // The interaction that opened this dialog is frequently still propagating.
    // On touch devices the open fires from an earlier event in the sequence
    // (pointerdown/touchend) than the trailing `click`, so a handler attached
    // here receives that very same click, sees a target outside the <dialog>
    // element, and closes what just opened. That is what made the cart badge a
    // dead tap on mobile: the drawer opened and shut inside one millisecond.
    // Add-to-cart was unaffected only because it awaits its fetch first, so its
    // click has long finished propagating by the time this runs.
    //
    // setTimeout, deliberately, and NOT requestAnimationFrame: timers still fire
    // in a backgrounded tab while frames do not, and not depending on frames is
    // the entire point of opening synchronously above.
    setTimeout(() => {
      // The dialog may already have been dismissed before this task ran; adding
      // the handlers then would leave them attached to a closed dialog.
      if (!dialog.open) return;

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    }, 0);
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    // `dialog.open` stays true for the whole closing animation, so it cannot be
    // the only re-entrancy guard: a second call landing during that window used
    // to run the entire teardown a second time and, critically, dispatch a
    // second DialogCloseEvent. On the cart drawer that means two history.back()
    // calls and the shopper is thrown two entries back, off the page. Reachable
    // from the close button plus Esc/outside-click in quick succession, from
    // #handleResize, and from #handlePopState.
    if (!dialog.open || this.#closing) return;
    this.#closing = true;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    // Let the closing animation play, but never let it become load-bearing.
    // onAnimationEnd resolves off Animation.finished, which never settles while
    // the browser withholds frames (backgrounded tab, in-app browsers, heavy
    // throttling) - the same starvation that used to keep the dialog from
    // opening. Without the race, that stalls here forever with body still
    // position:fixed, leaving the page scroll-locked under a dialog that never
    // closes. Whichever finishes first, the teardown below runs exactly once.
    await Promise.race([
      onAnimationEnd(dialog, undefined, { subtree: false }),
      new Promise((resolve) => setTimeout(resolve, CLOSE_ANIMATION_TIMEOUT)),
    ]);

    // Teardown, in one synchronous block. Nothing may await between these
    // statements and the order is load-bearing:
    //
    // 1. dialog.close() first, so the modal has left the top layer before we
    //    touch the scroller. It also returns focus to whatever was focused
    //    before the dialog opened, and that focus restoration can itself scroll
    //    the page - doing it here means our restore below is the last word
    //    rather than being overwritten by it.
    // 2. Release the scroll lock. The visual offset came entirely from
    //    top:-scrollY while position:fixed, so the instant it is removed the
    //    document is really at offset 0.
    // 3. Restore the scroll in the very next statement, before the browser can
    //    paint offset 0. `behavior: 'instant'` is required, not cosmetic:
    //    base.css sets `html { scroll-behavior: smooth }`, so without it this
    //    becomes an animated glide from the top instead of a restore.
    dialog.close();

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.classList.remove('dialog-closing');
    this.#closing = false;

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
